import rafSchedule from 'raf-schd';
import type { IntlShape } from 'react-intl';

import { getDocument } from '@atlaskit/browser-apis';
import {
	ACTION,
	ACTION_SUBJECT,
	ACTION_SUBJECT_ID,
	EVENT_TYPE,
} from '@atlaskit/editor-common/analytics';
import { getBrowserInfo } from '@atlaskit/editor-common/browser';
import { getNodeIdProvider } from '@atlaskit/editor-common/node-anchor';
import {
	isMeasuring,
	startMeasure,
	stopMeasure,
} from '@atlaskit/editor-common/performance-measures';
import type { PortalProviderAPI } from '@atlaskit/editor-common/portal';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import { DRAG_HANDLE_SELECTOR } from '@atlaskit/editor-common/styles';
import { areToolbarFlagsEnabled } from '@atlaskit/editor-common/toolbar-flag-check';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { EDIT_AREA_ID } from '@atlaskit/editor-common/ui';
import { isEmptyDocument } from '@atlaskit/editor-common/utils';
import type {
	EditorState,
	ReadonlyTransaction,
	Transaction,
} from '@atlaskit/editor-prosemirror/state';
import { PluginKey, TextSelection } from '@atlaskit/editor-prosemirror/state';
import { DecorationSet } from '@atlaskit/editor-prosemirror/view';
import type { Decoration, EditorView } from '@atlaskit/editor-prosemirror/view';
import { fg } from '@atlaskit/platform-feature-flags';
import { autoScrollForElements } from '@atlaskit/pragmatic-drag-and-drop-auto-scroll/element';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import type { CleanupFn } from '@atlaskit/pragmatic-drag-and-drop/types';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';

import type {
	ActiveDropTargetNode,
	BlockControlsMeta,
	BlockControlsPlugin,
	MultiSelectDnD,
	NodeDecorationFactory,
	PluginState,
} from '../blockControlsPluginType';
import { getAnchorAttrName } from '../ui/utils/dom-attr-name';

import { findNodeDecs, nodeDecorations } from './decorations-anchor';
import {
	createActiveDragHandleNodeDecoration,
	dragHandleDecoration,
	emptyParagraphNodeDecorations,
	findActiveDragHandleNodeDec,
	findHandleDec,
} from './decorations-drag-handle';
import { dropTargetDecorations, findDropTargetDecs } from './decorations-drop-target';
import { getActiveDropTargetDecorations } from './decorations-drop-target-active';
import {
	createActiveQuickInsertNodeDecoration,
	findActiveQuickInsertNodeDec,
	findQuickInsertInsertButtonDecoration,
	quickInsertButtonDecoration,
} from './decorations-quick-insert-button';
import { handleMouseDown } from './handle-mouse-down';
import { handleMouseOver } from './handle-mouse-over';
import { boundKeydownHandler } from './keymap';
import { defaultActiveAnchorTracker } from './utils/active-anchor-tracker';
import { getMultiSelectAnalyticsAttributes } from './utils/analytics';
import { AnchorRectCache, isAnchorSupported } from './utils/anchor-utils';
import { selectNode } from './utils/getSelection';
import { getSelectedSlicePosition } from './utils/selection';
import { getTrMetadata } from './utils/transactions';

export const key: PluginKey<PluginState> = new PluginKey<PluginState>('blockControls');

const EDITOR_BLOCKS_DRAG_INIT = 'Editor Blocks Drag Initialization Time';
const EDITOR_BLOCKS_DROP_INIT = 'Editor Blocks Drop Initialization Time';

const scheduleCallback = (cb: () => unknown, options?: IdleRequestOptions) => {
	return 'requestIdleCallback' in window
		? requestIdleCallback(cb, { timeout: 5000, ...options })
		: requestAnimationFrame(cb);
};

type ElementDragSource = {
	start: number;
	// drag and drop exists for other nodes (e.g. tables), use type === 'element' to
	// filter out
	type: string;
};

const isHTMLElement = (element: Element | EventTarget | null): element is HTMLElement => {
	return element instanceof HTMLElement;
};

const destroyFn = (
	api: ExtractInjectionAPI<BlockControlsPlugin> | undefined,
	editorView?: EditorView,
) => {
	const scrollable = getDocument()?.querySelector('.fabric-editor-popup-scroll-parent') ?? null;

	const cleanupFn: CleanupFn[] = [];

	if (scrollable) {
		cleanupFn.push(
			autoScrollForElements({
				element: scrollable,
			}),
		);
	}

	let dragInitializationDuration = 0;

	cleanupFn.push(
		monitorForElements({
			canMonitor: ({ source }) => source.data.type === 'element',
			onDrag: () => {
				if (fg('platform_editor_drag_and_drop_perf_analytics')) {
					if (isMeasuring(EDITOR_BLOCKS_DRAG_INIT)) {
						stopMeasure(EDITOR_BLOCKS_DRAG_INIT, (duration: number) => {
							dragInitializationDuration = duration;
						});
					}
				} else {
					if (isMeasuring(EDITOR_BLOCKS_DRAG_INIT)) {
						stopMeasure(EDITOR_BLOCKS_DRAG_INIT, (duration: number, startTime: number) => {
							api?.analytics?.actions.fireAnalyticsEvent({
								action: ACTION.BLOCKS_DRAG_INIT,
								actionSubject: ACTION_SUBJECT.EDITOR,
								eventType: EVENT_TYPE.OPERATIONAL,
								attributes: {
									duration,
									startTime,
									nodesCount: editorView?.state.doc.nodeSize,
								},
							});
						});
					}
				}
			},
			onDragStart: () => {
				if (isHTMLElement(scrollable)) {
					scrollable.style.setProperty('scroll-behavior', 'unset');
				}
			},
			onDrop: ({ location, source }) => {
				if (isHTMLElement(scrollable)) {
					scrollable.style.setProperty('scroll-behavior', null);
				}
				if (fg('platform_editor_drag_and_drop_perf_analytics')) {
					startMeasure(EDITOR_BLOCKS_DROP_INIT);
				}

				if (!api) {
					return;
				}

				api.core?.actions.execute(({ tr }) => {
					const { multiSelectDnD } = api.blockControls?.sharedState.currentState() || {};
					// Restore the users initial Editor selection when the drop completes
					if (multiSelectDnD) {
						// If the TextSelection between the drag start and end has changed, the document has changed, and we should not reapply the last selection
						const expandedSelectionUnchanged =
							multiSelectDnD.textAnchor === tr.selection.anchor &&
							multiSelectDnD.textHead === tr.selection.head;

						if (expandedSelectionUnchanged) {
							const $anchor = tr.doc.resolve(multiSelectDnD.userAnchor);
							const $head = tr.doc.resolve(multiSelectDnD.userHead);

							if ($head.node() === $anchor.node()) {
								const $from = $anchor.min($head);
								selectNode(tr, $from.pos, $from.node().type.name, api);
							} else {
								tr.setSelection(
									TextSelection.create(tr.doc, multiSelectDnD.userAnchor, multiSelectDnD.userHead),
								);
							}
						}
					}
					api.selection?.commands.clearManualSelection()({ tr });

					const { start } = source.data as ElementDragSource;
					// if no drop targets are rendered, assume that drop is invalid
					const lastDragCancelled = location.current.dropTargets.length === 0;
					if (lastDragCancelled) {
						const position = getSelectedSlicePosition(start, tr, api);
						const attributes = getMultiSelectAnalyticsAttributes(tr, position.from, position.to);
						const { nodeTypes, hasSelectedMultipleNodes } = attributes;

						const resolvedMovingNode = tr.doc.resolve(start);
						api.analytics?.actions.attachAnalyticsEvent({
							eventType: EVENT_TYPE.UI,
							action: ACTION.CANCELLED,
							actionSubject: ACTION_SUBJECT.DRAG,
							actionSubjectId: ACTION_SUBJECT_ID.ELEMENT_DRAG_HANDLE,
							attributes: {
								nodeDepth: resolvedMovingNode.depth,
								nodeTypes: nodeTypes || '',
								hasSelectedMultipleNodes,
							},
						})(tr);
					}

					if (fg('platform_editor_ease_of_use_metrics')) {
						api.metrics?.commands.startActiveSessionTimer()({ tr });
					}

					api.userIntent?.commands.setCurrentUserIntent('default')({ tr });

					return tr.setMeta(key, {
						...tr.getMeta(key),
						isDragging: false,
						isPMDragging: false,
						lastDragCancelled,
					});
				});

				if (fg('platform_editor_drag_and_drop_perf_analytics')) {
					// wait for the idle callback to ensure that the drag operation has completed
					scheduleCallback(() => {
						if (isMeasuring(EDITOR_BLOCKS_DROP_INIT)) {
							stopMeasure(EDITOR_BLOCKS_DROP_INIT, (duration: number) => {
								const isCancelled = location.current.dropTargets.length === 0;
								api?.analytics?.actions.fireAnalyticsEvent({
									action: ACTION.ENDED,
									eventType: EVENT_TYPE.TRACK,
									actionSubject: ACTION_SUBJECT.DRAG,
									actionSubjectId: ACTION_SUBJECT_ID.ELEMENT_DRAG_HANDLE,
									attributes: {
										dragInitializationDuration,
										dropProcessingDuration: duration,
										isCancelled,
										nodesCount: editorView?.state.doc.nodeSize,
									},
								});
							});
						}
					});
				}
			},
		}),
	);

	return combine(...cleanupFn);
};

const initialState: PluginState = {
	decorations: DecorationSet.empty,
	activeNode: undefined,
	isDragging: false,
	isMenuOpen: false,
	editorHeight: 0,
	editorWidthLeft: 0,
	editorWidthRight: 0,
	isResizerResizing: false,
	isDocSizeLimitEnabled: null,
	isPMDragging: false,
	multiSelectDnD: undefined,
	lastDragCancelled: false,
	isSelectedViaDragHandle: false,
};

export interface FlagType {
	toolbarFlagsEnabled: boolean;
}

export const getDecorations = (state: EditorState): DecorationSet | undefined =>
	key.getState(state)?.decorations;

const getDecorationAtPos = (
	state: EditorState,
	decorations: DecorationSet,
	pos: number,
	to: number,
) => {
	// Find the newly minted node decs that touch the active node
	const findNewNodeDecs = findNodeDecs(state, decorations, pos - 1, to);

	// Find the specific dec that the active node corresponds to
	const nodeDecsAtActivePos = findNewNodeDecs.filter((dec: Decoration) => dec?.from === pos);

	// If multiple decorations at the active node pos, we want the last one
	const nodeDecAtActivePos = nodeDecsAtActivePos.pop();

	return nodeDecAtActivePos;
};

export const apply = (
	api: ExtractInjectionAPI<BlockControlsPlugin> | undefined,
	formatMessage: IntlShape['formatMessage'],
	tr: ReadonlyTransaction,
	currentState: PluginState,
	newState: EditorState,
	flags: FlagType,
	nodeViewPortalProviderAPI: PortalProviderAPI,
	nodeDecorationRegistry: NodeDecorationFactory[],
	rightSideControlsEnabled = false,
	quickInsertButtonEnabled = true,
	anchorRectCache?: AnchorRectCache,
	resizeObserverWidth?: ResizeObserver,
	pragmaticCleanup?: (() => void) | null,
):
	| PluginState
	| {
			activeDropTargetNode: ActiveDropTargetNode | undefined;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			activeNode: any;
			blockMenuOptions:
				| {
						// eslint-disable-next-line @typescript-eslint/no-explicit-any
						canMoveDown: any;
						// eslint-disable-next-line @typescript-eslint/no-explicit-any
						canMoveUp: any;
						// eslint-disable-next-line @typescript-eslint/no-explicit-any
						openedViaKeyboard: any;
				  }
				| undefined;
			decorations: DecorationSet;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			editorHeight: any;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			editorWidthLeft: any;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			editorWidthRight: any;
			isDocSizeLimitEnabled: boolean | null;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			isDragging: any;
			isMenuOpen: boolean | undefined;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			isPMDragging: any;
			isResizerResizing: boolean;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			isSelectedViaDragHandle: any;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			isShiftDown: any;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			lastDragCancelled: any;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			menuTriggerBy: any;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			menuTriggerByNode: any;
			multiSelectDnD: MultiSelectDnD | undefined;
	  } => {
	let { activeNode, decorations, isResizerResizing, multiSelectDnD } = currentState;
	const {
		editorHeight,
		editorWidthLeft,
		editorWidthRight,
		isDragging,
		isMenuOpen,
		menuTriggerBy,
		menuTriggerByNode,
		blockMenuOptions,
		isPMDragging,
		isShiftDown,
		lastDragCancelled,
		isSelectedViaDragHandle,
	} = currentState;
	let isActiveNodeDeleted = false;
	const { from, to, numReplaceSteps, isAllText, isReplacedWithSameSize } = getTrMetadata(tr, flags);
	const meta = tr.getMeta(key);

	const hasDocumentSizeBreachedThreshold = api?.limitedMode?.sharedState
		.currentState()
		?.limitedModePluginKey.getState(newState)?.documentSizeBreachesThreshold;

	if (hasDocumentSizeBreachedThreshold) {
		/**
		 * INFO: This if statement is a duplicate of the logic in destroy(). When the threshold is breached and we enter limited mode, we want to trigger the cleanup logic in destroy().
		 */
		const editorContentArea =
			getDocument()?.querySelector('.fabric-editor-popup-scroll-parent') ?? null;

		if (editorContentArea && resizeObserverWidth) {
			resizeObserverWidth.unobserve(editorContentArea);
		}

		pragmaticCleanup?.();

		return currentState;
	}

	// When steps exist, remap existing decorations, activeNode and multi select positions
	if (tr.docChanged) {
		decorations = decorations.map(tr.mapping, tr.doc);

		// platform_editor_controls note: enables quick insert
		// don't remap activeNode if it's being dragged
		if (!flags.toolbarFlagsEnabled) {
			if (activeNode) {
				const mappedPos = tr.mapping.mapResult(activeNode.pos);
				isActiveNodeDeleted = mappedPos.deleted;
				activeNode = {
					pos: mappedPos.pos,
					anchorName: activeNode.anchorName,
					nodeType: activeNode.nodeType,
				};
			}
		} else {
			if (activeNode && meta?.isDragging !== true) {
				let mappedPos;
				const browser = getBrowserInfo();
				// In safari, when platform_editor_controls is on,
				// sometimes the drag handle for the layout disppears after you click on the handle for a few times
				// Which caused the drag handle onClick event not firing, then block menu wouldn't be opened
				// This is caused by the mappedPos.deletedAfter sometimes returning true in webkit browsers even though the active node still exists
				// This is likely a prosemirror and safari integration bug, but to unblock the issue, we are going to use mappedPos.deleted in safari for now
				if (browser.webkit && editorExperiment('platform_editor_block_menu', true)) {
					mappedPos = tr.mapping.mapResult(activeNode.pos);
					isActiveNodeDeleted = mappedPos.deleted;
				} else {
					mappedPos = tr.mapping.mapResult(activeNode.pos, -1);
					isActiveNodeDeleted = mappedPos.deletedAfter;
				}
				// for editor controls, remap the rootPos as well
				let mappedRootPos;
				if (activeNode.rootPos !== undefined) {
					mappedRootPos = tr.mapping.mapResult(activeNode.rootPos, -1);
				}

				activeNode = {
					pos: mappedPos.pos,
					anchorName: activeNode.anchorName,
					nodeType: activeNode.nodeType,
					rootPos: mappedRootPos?.pos ?? activeNode.rootPos,
					rootAnchorName: activeNode.rootAnchorName,
					rootNodeType: activeNode.rootNodeType,
				};
			}
		}

		if (multiSelectDnD) {
			multiSelectDnD.anchor = tr.mapping.map(multiSelectDnD.anchor);
			multiSelectDnD.head = tr.mapping.map(multiSelectDnD.head);
		}
	}

	const resizerMeta = tr.getMeta('is-resizer-resizing');
	isResizerResizing = resizerMeta ?? isResizerResizing;
	const hasJustFinishedResizing = resizerMeta === false;

	multiSelectDnD = meta?.multiSelectDnD ?? multiSelectDnD;

	if (multiSelectDnD) {
		if (
			(meta?.isDragging ?? isDragging) &&
			expValEquals('platform_editor_block_controls_perf_optimization', 'isEnabled', true)
		) {
			// When dragging, we want to keep the multiSelectDnD object unless both document and selection
			// has changed, in which case we want to reset it.
			const shouldResetMultiSelectDnD = tr.docChanged && tr.selectionSet && tr.selection.empty;
			multiSelectDnD = shouldResetMultiSelectDnD ? undefined : multiSelectDnD;
		} else {
			multiSelectDnD =
				meta?.isDragging === false || tr.selection.empty ? undefined : multiSelectDnD;
		}
	}

	const maybeNodeCountChanged = !isAllText && numReplaceSteps > 0;
	let latestActiveNode = meta?.activeNode;
	const isViewMode = api?.editorViewMode?.sharedState.currentState()?.mode === 'view';

	if (!latestActiveNode && (!isActiveNodeDeleted || isReplacedWithSameSize)) {
		latestActiveNode = activeNode;
	}

	// Re-create node decorations
	const isDecSetEmpty = decorations === DecorationSet.empty;
	const isNodeDecsMissing =
		isDecSetEmpty ||
		maybeNodeCountChanged ||
		(editorExperiment('platform_editor_breakout_resizing', true) && hasJustFinishedResizing);
	const shouldRedrawNodeDecs =
		!isResizerResizing &&
		(isNodeDecsMissing || meta?.isDragging) &&
		// Skip expensive anchor node decoration recalculations when native anchor support is enabled
		!expValEquals('platform_editor_native_anchor_with_dnd', 'isEnabled', true);

	let isActiveNodeModified = false;

	if (api && shouldRedrawNodeDecs) {
		const oldNodeDecs = findNodeDecs(newState, decorations, from, to);
		decorations = decorations.remove(oldNodeDecs);
		const newNodeDecs = nodeDecorations(
			newState,
			isDecSetEmpty ? undefined : from,
			isDecSetEmpty ? undefined : to,
		);
		decorations = decorations.add(newState.doc, newNodeDecs);

		// platform_editor_controls note: enables quick insert
		if (!flags.toolbarFlagsEnabled) {
			if (latestActiveNode && !isActiveNodeDeleted) {
				// Find the newly minted node decs that touch the active node
				const findNewNodeDecs = findNodeDecs(newState, decorations, latestActiveNode.pos - 1, to);

				// Find the specific dec that the active node corresponds to
				const nodeDecsAtActivePos = findNewNodeDecs.filter(
					(dec: Decoration) => dec?.from === latestActiveNode.pos,
				);

				// If multiple decorations at the active node pos, we want the last one
				const nodeDecAtActivePos = nodeDecsAtActivePos.pop();

				// Update the active node anchor-name and type for accurate positioning
				if (nodeDecAtActivePos) {
					isActiveNodeModified = true;
					latestActiveNode.anchorName = nodeDecAtActivePos.spec.anchorName;
					latestActiveNode.nodeType = nodeDecAtActivePos.spec.nodeType;
				}
			}
		} else {
			if (latestActiveNode && (!isActiveNodeDeleted || isReplacedWithSameSize)) {
				const nodeDecAtActivePos = getDecorationAtPos(
					newState,
					decorations,
					latestActiveNode.pos,
					to,
				);
				const rootNodeDecAtActivePos =
					latestActiveNode.rootPos !== undefined
						? getDecorationAtPos(newState, decorations, latestActiveNode.rootPos, to)
						: undefined;

				if (nodeDecAtActivePos || rootNodeDecAtActivePos) {
					isActiveNodeModified = true;
				}

				// Update the active node anchor-name and type for accurate positioning
				if (nodeDecAtActivePos) {
					latestActiveNode.anchorName = nodeDecAtActivePos.spec.anchorName;
					latestActiveNode.nodeType = nodeDecAtActivePos.spec.nodeType;
				}

				if (rootNodeDecAtActivePos) {
					latestActiveNode.rootAnchorName = rootNodeDecAtActivePos.spec.anchorName;
					latestActiveNode.rootNodeType = rootNodeDecAtActivePos.spec.nodeType;
				}
			}
		}
	}

	// Check if editor dimensions have changed
	const editorSizeChanged =
		(meta?.editorHeight !== undefined && meta?.editorHeight !== editorHeight) ||
		(meta?.editorWidthLeft !== undefined && meta?.editorWidthLeft !== editorWidthLeft) ||
		(meta?.editorWidthRight !== undefined && meta?.editorWidthRight !== editorWidthRight);

	// Check if there's a new active node, and it differs from the last
	const activeNodeChanged =
		meta?.activeNode &&
		((meta?.activeNode.pos !== activeNode?.pos &&
			meta?.activeNode.anchorName !== activeNode?.anchorName) ||
			meta?.activeNode.handleOptions?.isFocused);

	const rootActiveNodeChanged =
		meta?.activeNode &&
		meta?.activeNode.rootPos !== activeNode?.rootPos &&
		meta?.activeNode.rootAnchorName !== activeNode?.rootAnchorName;

	// Some browsers don't support anchor positioning, meaning we need to replace the handle when nodes change
	const handleNeedsRedraw = shouldRedrawNodeDecs && !isAnchorSupported();

	// Create/recreate handle dec when the active node is missing/changes, or the editor viewport has changed (non-anchor pos workaround)
	const shouldRecreateHandle =
		latestActiveNode &&
		(activeNodeChanged || isActiveNodeModified || editorSizeChanged || handleNeedsRedraw);

	const shouldRecreateQuickInsertButton =
		latestActiveNode &&
		(rootActiveNodeChanged || isActiveNodeModified || editorSizeChanged || handleNeedsRedraw);

	// If the active node is missing, we need to remove the handle
	let shouldRemoveHandle = latestActiveNode
		? isResizerResizing || (isActiveNodeDeleted && !isReplacedWithSameSize) || meta?.nodeMoved
		: true;

	// platform_editor_controls note: enables quick insert
	if (flags.toolbarFlagsEnabled) {
		// Remove handle dec when editor is blurred
		shouldRemoveHandle = shouldRemoveHandle || meta?.editorBlurred;
	}

	// In view mode with right-side controls, remove any lingering drag handle decorations
	// (they may carry over from edit mode). Only remove drag handles specifically, not
	// the remix button decorations (those are managed separately via showInViewMode).
	if (isViewMode && rightSideControlsEnabled) {
		const allHandleDecs = findHandleDec(decorations, 0, newState.doc.content.size);
		if (allHandleDecs.length > 0) {
			decorations = decorations.remove(allHandleDecs);
		}
	}

	if (shouldRemoveHandle) {
		const oldHandle = findHandleDec(decorations, activeNode?.pos, activeNode?.pos);
		decorations = decorations.remove(oldHandle);
		// When removing the handle, also remove the anchor-marker node decorations
		// (data-active-drag-handle / data-active-quick-insert) so the DOM attributes
		// don't linger on nodes that are no longer active.
		// Use full-range search (0..contentSize) rather than a point-range at activeNode.pos/rootPos:
		// after a doc change (e.g. pressing Enter splits a node), DecorationSet.map() can shift the
		// decoration to a different position, so a point-range search would miss it and leave a stale
		// attribute on the wrong DOM node.
		if (expValEquals('platform_editor_controls_reliable_anchor', 'isEnabled', true)) {
			const oldActiveNodeDec = findActiveDragHandleNodeDec(
				decorations,
				0,
				newState.doc.content.size,
			);
			decorations = decorations.remove(oldActiveNodeDec);
			const oldActiveQuickInsertDec = findActiveQuickInsertNodeDec(
				decorations,
				0,
				newState.doc.content.size,
			);
			decorations = decorations.remove(oldActiveQuickInsertDec);
		}
		// platform_editor_controls note: enables quick insert
		if (flags.toolbarFlagsEnabled && quickInsertButtonEnabled) {
			const oldQuickInsertButton = findQuickInsertInsertButtonDecoration(
				decorations,
				activeNode?.rootPos,
				activeNode?.rootPos,
			);
			decorations = decorations.remove(oldQuickInsertButton);
			for (const factory of nodeDecorationRegistry) {
				const old = decorations.find(
					activeNode?.rootPos,
					activeNode?.rootPos,
					(spec) => spec.type === factory.type,
				);
				decorations = decorations.remove(old);
			}
			if (
				rightSideControlsEnabled &&
				isViewMode &&
				fg('confluence_remix_button_right_side_block_fg')
			) {
				for (const factory of nodeDecorationRegistry) {
					if (factory.showInViewMode) {
						const old = decorations.find(
							activeNode?.rootPos,
							activeNode?.rootPos,
							(spec) => spec.type === factory.type,
						);
						decorations = decorations.remove(old);
					}
				}
			}
		}
	} else if (api) {
		if (shouldRecreateHandle && (!rightSideControlsEnabled || !isViewMode)) {
			const oldHandle = findHandleDec(decorations, activeNode?.pos, activeNode?.pos);
			decorations = decorations.remove(oldHandle);

			const handleDec = dragHandleDecoration({
				api,
				formatMessage,
				pos: latestActiveNode?.pos,
				anchorName: latestActiveNode?.anchorName,
				nodeType: latestActiveNode?.nodeType,
				handleOptions: latestActiveNode?.handleOptions,
				nodeViewPortalProviderAPI,
				anchorRectCache,
				editorState: newState,
			});

			decorations = decorations.add(newState.doc, [handleDec]);
		}

		if (
			expValEquals('platform_editor_controls_reliable_anchor', 'isEnabled', true) &&
			latestActiveNode
		) {
			// Recreate the drag handle node decoration when the active node changed,
			// its content was modified, or the document changed (e.g. Enter splits a node).
			// This runs independently of shouldRecreateHandle so that doc changes (like pressing
			// Enter) correctly refresh the decoration even when the handle widget doesn't move.
			// DecorationSet.map() can misplace the decoration after node splits/inserts.
			const needsNodeDecUpdate = activeNodeChanged || isActiveNodeModified || tr.docChanged;
			if (needsNodeDecUpdate) {
				const nodeSize = newState.doc.nodeAt(latestActiveNode.pos)?.nodeSize;
				if (nodeSize !== undefined) {
					// Search the full doc range: after DecorationSet.map(), the decoration may have
					// shifted away from activeNode?.pos (e.g. a node split moves it to the new paragraph).
					const oldActiveNodeDec = findActiveDragHandleNodeDec(
						decorations,
						0,
						newState.doc.content.size,
					);
					// Skip remove+add if the decoration is already at the correct position.
					// On pure text keystrokes, DecorationSet.map() keeps the decoration correctly
					// placed, so we avoid unnecessary churn on every character typed.
					const dragHandleDecAlreadyCorrect =
						oldActiveNodeDec.length === 1 &&
						oldActiveNodeDec[0].from === latestActiveNode.pos &&
						oldActiveNodeDec[0].to === latestActiveNode.pos + nodeSize;
					if (!dragHandleDecAlreadyCorrect) {
						decorations = decorations.remove(oldActiveNodeDec);
						decorations = decorations.add(newState.doc, [
							createActiveDragHandleNodeDecoration(latestActiveNode.pos, nodeSize),
						]);
					}
				}
			}

			// The quick-insert decoration lives on the root node (rootPos), which can change
			// independently of the edit-mode node — e.g. when only editorSizeChanged fires but
			// rootActiveNodeChanged is also true. So we use a separate, broader guard that
			// includes rootActiveNodeChanged to avoid leaving the attribute on a stale root node.
			// tr.docChanged is included for the same reason as needsNodeDecUpdate: DecorationSet.map()
			// can misplace the decoration after a node split, so we must refresh it on every doc change.
			const needsQuickInsertDecUpdate =
				activeNodeChanged || isActiveNodeModified || rootActiveNodeChanged || tr.docChanged;
			if (needsQuickInsertDecUpdate && latestActiveNode.rootPos !== undefined) {
				const rootNodeSize = newState.doc.nodeAt(latestActiveNode.rootPos)?.nodeSize;
				if (rootNodeSize !== undefined) {
					const oldActiveQuickInsertDec = findActiveQuickInsertNodeDec(
						decorations,
						0,
						newState.doc.content.size,
					);
					// Skip remove+add if the decoration is already at the correct position.
					// On pure text keystrokes, DecorationSet.map() keeps the decoration correctly
					// placed, so we avoid unnecessary churn on every character typed.
					const quickInsertDecAlreadyCorrect =
						oldActiveQuickInsertDec.length === 1 &&
						oldActiveQuickInsertDec[0].from === latestActiveNode.rootPos &&
						oldActiveQuickInsertDec[0].to === latestActiveNode.rootPos + rootNodeSize;
					if (!quickInsertDecAlreadyCorrect) {
						decorations = decorations.remove(oldActiveQuickInsertDec);
						decorations = decorations.add(newState.doc, [
							createActiveQuickInsertNodeDecoration(latestActiveNode.rootPos, rootNodeSize),
						]);
					}
				}
			}
		}

		if (
			shouldRecreateQuickInsertButton &&
			latestActiveNode?.rootPos !== undefined &&
			// platform_editor_controls note: enables quick insert
			flags.toolbarFlagsEnabled &&
			quickInsertButtonEnabled &&
			(!rightSideControlsEnabled || !isViewMode)
		) {
			const oldQuickInsertButton = findQuickInsertInsertButtonDecoration(
				decorations,
				activeNode?.rootPos,
				activeNode?.rootPos,
			);
			decorations = decorations.remove(oldQuickInsertButton);

			const quickInsertButton = quickInsertButtonDecoration({
				api,
				formatMessage,
				anchorName: latestActiveNode?.anchorName,
				nodeType: latestActiveNode?.nodeType,
				nodeViewPortalProviderAPI,
				rootPos: latestActiveNode?.rootPos,
				rootAnchorName: latestActiveNode?.rootAnchorName,
				rootNodeType: latestActiveNode?.rootNodeType,
				anchorRectCache,
				editorState: newState,
			});
			decorations = decorations.add(newState.doc, [quickInsertButton]);

			// Update quick insert node decoration when the quick insert button is recreated but
			// the drag handle was NOT recreated (shouldRecreateHandle was false). When
			// shouldRecreateHandle is true, the block above already handles this.
			// Also skip when the needsQuickInsertDecUpdate block above already handled the update
			// (i.e. activeNodeChanged || isActiveNodeModified || rootActiveNodeChanged || tr.docChanged)
			// — running both would remove and re-add a duplicate decoration.
			if (
				expValEquals('platform_editor_controls_reliable_anchor', 'isEnabled', true) &&
				!shouldRecreateHandle &&
				!(activeNodeChanged || isActiveNodeModified || rootActiveNodeChanged || tr.docChanged) &&
				latestActiveNode?.rootPos !== undefined
			) {
				// Use full-range search (0..contentSize) rather than a point-range at activeNode.rootPos:
				// after a doc change, DecorationSet.map() can shift the decoration to a different position,
				// so a point-range search would miss it and leave a stale attribute on the wrong DOM node.
				const oldActiveQuickInsertDec = findActiveQuickInsertNodeDec(
					decorations,
					0,
					newState.doc.content.size,
				);
				decorations = decorations.remove(oldActiveQuickInsertDec);
				const rootNodeSize = newState.doc.nodeAt(latestActiveNode.rootPos)?.nodeSize;
				if (rootNodeSize !== undefined) {
					decorations = decorations.add(newState.doc, [
						createActiveQuickInsertNodeDecoration(latestActiveNode.rootPos, rootNodeSize),
					]);
				}
			}

			if (rightSideControlsEnabled) {
				for (const factory of nodeDecorationRegistry) {
					if (!latestActiveNode || latestActiveNode.rootPos === undefined) {
						continue;
					}
					const params = {
						editorState: newState,
						nodeViewPortalProviderAPI,
						anchorName: latestActiveNode.anchorName,
						nodeType: latestActiveNode.nodeType,
						rootPos: latestActiveNode.rootPos,
						rootAnchorName: latestActiveNode.rootAnchorName,
						rootNodeType: latestActiveNode.rootNodeType,
					};
					const old = decorations.find(
						activeNode?.rootPos,
						activeNode?.rootPos,
						(spec) => spec.type === factory.type,
					);
					decorations = decorations.remove(old);

					// determines whether to show the decorations, see malleableUiPlugin.tsx
					if (factory.shouldCreate && !factory.shouldCreate(params)) {
						continue;
					}
					const dec = factory.create(params);
					decorations = decorations.add(newState.doc, [dec]);
				}
			} else {
				for (const factory of nodeDecorationRegistry) {
					const old = decorations.find(
						0,
						newState.doc.nodeSize,
						(spec) => spec.type === factory.type,
					);
					decorations = decorations.remove(old);
				}
			}
		}

		// In view mode (edit/live pages), show right-side controls on block hover (without drag handle or quick insert)
		const rootPos = latestActiveNode?.rootPos;
		// rootPos is computed using the same logic as the floating insert menu, so it always points to a top-level (doc child) block when defined.
		const isDocLevel = rootPos !== undefined && !isNaN(rootPos);
		if (isViewMode && isDocLevel && flags.toolbarFlagsEnabled && rightSideControlsEnabled) {
			for (const factory of nodeDecorationRegistry) {
				if (factory.showInViewMode) {
					if (!latestActiveNode || latestActiveNode.rootPos === undefined) {
						continue;
					}
					const params = {
						editorState: newState,
						nodeViewPortalProviderAPI,
						anchorName: latestActiveNode.anchorName,
						nodeType: latestActiveNode.nodeType,
						rootPos: latestActiveNode.rootPos,
						rootAnchorName: latestActiveNode.rootAnchorName,
						rootNodeType: latestActiveNode.rootNodeType,
					};
					if (factory.shouldCreate && !factory.shouldCreate(params)) {
						continue;
					}
					const existingAtPos = decorations.find(
						rootPos,
						rootPos,
						(spec) => spec.type === factory.type,
					);
					// Skip remove/re-add when decoration already exists at correct position - avoids
					// flickering from widget destroy/recreate on every transaction (e.g. on hover).
					if (existingAtPos.length > 0) {
						continue;
					}
					// Remove any stale decoration at a different position (e.g. after moving to another block)
					const stale = decorations.find(
						0,
						newState.doc.nodeSize,
						(spec) => spec.type === factory.type,
					);
					decorations = decorations.remove(stale);
					const dec = factory.create(params);
					decorations = decorations.add(newState.doc, [dec]);
				}
			}
		} else if (
			isViewMode &&
			rightSideControlsEnabled &&
			fg('confluence_remix_button_right_side_block_fg')
		) {
			// Remove view-mode right-side decorations when no active node
			for (const factory of nodeDecorationRegistry) {
				if (factory.showInViewMode) {
					const old = decorations.find(
						0,
						newState.doc.nodeSize,
						(spec) => spec.type === factory.type,
					);
					decorations = decorations.remove(old);
				}
			}
		}
	}

	// Drop targets may be missing when the node count is being changed during a drag
	const isDropTargetsMissing =
		(meta?.isDragging ?? isDragging) && maybeNodeCountChanged && !meta?.nodeMoved;

	// Remove drop target decorations when dragging stops or they need to be redrawn
	if (meta?.isDragging === false || isDropTargetsMissing) {
		const dropTargetDecs = findDropTargetDecs(decorations);
		decorations = decorations.remove(dropTargetDecs);
	}

	let currentActiveDropTargetNode: ActiveDropTargetNode | undefined = isDragging
		? currentState.activeDropTargetNode
		: undefined;

	if (api) {
		if (expValEquals('platform_editor_block_controls_perf_optimization', 'isEnabled', true)) {
			// If page is updated while dragging (likely by remote updates), we simply remove the drop targets
			// and add them back when the user interacts with the editor again
			if (isDropTargetsMissing) {
				const oldDropTargetDecs = findDropTargetDecs(decorations);
				decorations = decorations.remove(oldDropTargetDecs);
			}

			if (meta?.activeDropTargetNode) {
				currentActiveDropTargetNode = meta?.activeDropTargetNode as ActiveDropTargetNode;
				const oldDropTargetDecs = findDropTargetDecs(decorations);

				const { decsToAdd, decsToRemove } = getActiveDropTargetDecorations(
					meta.activeDropTargetNode,
					newState,
					api,
					oldDropTargetDecs,
					formatMessage,
					nodeViewPortalProviderAPI,
					latestActiveNode,
					anchorRectCache,
				);

				decorations = decorations.remove(decsToRemove);

				if (decsToAdd.length > 0) {
					decorations = decorations.add(newState.doc, decsToAdd);
				}
			}
		} else if (meta?.isDragging || isDropTargetsMissing) {
			// Add drop targets when dragging starts or some are missing
			const decs = dropTargetDecorations(
				newState,
				api,
				formatMessage,
				nodeViewPortalProviderAPI,
				latestActiveNode,
				anchorRectCache,
			);
			decorations = decorations.add(newState.doc, decs);
		}
	}

	const isEmptyDoc = isEmptyDocument(newState.doc);
	if (isEmptyDoc && !expValEquals('platform_editor_native_anchor_with_dnd', 'isEnabled', true)) {
		const hasNodeDecoration = !!findNodeDecs(newState, decorations).length;
		if (!hasNodeDecoration) {
			decorations = decorations.add(newState.doc, [emptyParagraphNodeDecorations()]);
		}
	}

	let newActiveNode;
	// platform_editor_controls note: enables quick insert
	if (flags.toolbarFlagsEnabled) {
		// remove isEmptyDoc check and let decorations render and determine their own visibility
		// In view mode with right-side controls we render node decorations (right-edge button), not the
		// handle - so findHandleDec is always empty. Don't clear activeNode in that case.
		const hasHandleOrViewModeControls =
			findHandleDec(decorations, latestActiveNode?.pos, latestActiveNode?.pos).length > 0 ||
			(isViewMode && rightSideControlsEnabled);
		newActiveNode =
			meta?.editorBlurred || (!meta?.activeNode && !hasHandleOrViewModeControls)
				? null
				: latestActiveNode;
	} else {
		newActiveNode =
			isEmptyDoc ||
			(!meta?.activeNode &&
				findHandleDec(decorations, latestActiveNode?.pos, latestActiveNode?.pos).length === 0)
				? null
				: latestActiveNode;
	}

	let isMenuOpenNew = isMenuOpen;
	if (editorExperiment('platform_editor_block_menu', true)) {
		if (meta?.closeMenu) {
			isMenuOpenNew = false;
		} else if (meta?.toggleMenu) {
			const isSameAnchor = meta?.toggleMenu.anchorName === menuTriggerBy;
			isMenuOpenNew =
				menuTriggerBy === undefined || isSameAnchor || (!isMenuOpen && !isSameAnchor)
					? !isMenuOpen
					: isMenuOpen;
		}
	} else if (meta?.toggleMenu) {
		isMenuOpenNew = !isMenuOpen;
	}

	let isSelectedViaDragHandleNew;
	if (flags.toolbarFlagsEnabled) {
		isSelectedViaDragHandleNew =
			meta?.isSelectedViaDragHandle !== undefined
				? meta?.isSelectedViaDragHandle
				: isSelectedViaDragHandle;
	} else {
		isSelectedViaDragHandleNew =
			meta?.isSelectedViaDragHandle !== undefined &&
			flags.toolbarFlagsEnabled &&
			meta?.isSelectedViaDragHandle;
	}

	return {
		decorations,
		activeNode: newActiveNode,
		activeDropTargetNode: currentActiveDropTargetNode,
		isDragging: meta?.isDragging ?? isDragging,
		isMenuOpen: isMenuOpenNew,
		menuTriggerBy:
			flags.toolbarFlagsEnabled || editorExperiment('platform_editor_block_menu', true)
				? meta?.toggleMenu?.anchorName || menuTriggerBy
				: undefined,
		menuTriggerByNode: meta?.toggleMenu?.triggerByNode || menuTriggerByNode,
		blockMenuOptions: editorExperiment('platform_editor_block_menu', true)
			? {
					canMoveUp:
						meta?.toggleMenu?.moveUp !== undefined
							? meta?.toggleMenu?.moveUp
							: blockMenuOptions?.canMoveUp,
					canMoveDown:
						meta?.toggleMenu?.moveDown !== undefined
							? meta?.toggleMenu?.moveDown
							: blockMenuOptions?.canMoveDown,
					openedViaKeyboard:
						meta?.toggleMenu?.openedViaKeyboard !== undefined
							? meta?.toggleMenu?.openedViaKeyboard
							: blockMenuOptions?.openedViaKeyboard,
				}
			: undefined,
		editorHeight: meta?.editorHeight ?? editorHeight,
		editorWidthLeft: meta?.editorWidthLeft ?? editorWidthLeft,
		editorWidthRight: meta?.editorWidthRight ?? editorWidthRight,
		isResizerResizing: isResizerResizing,
		isDocSizeLimitEnabled: initialState.isDocSizeLimitEnabled,
		isPMDragging: meta?.isPMDragging ?? isPMDragging,
		multiSelectDnD,
		isShiftDown: meta?.isShiftDown ?? isShiftDown,
		lastDragCancelled: meta?.lastDragCancelled ?? lastDragCancelled,
		isSelectedViaDragHandle: isSelectedViaDragHandleNew,
	};
};

export const createPlugin = (
	api: ExtractInjectionAPI<BlockControlsPlugin> | undefined,
	getIntl: () => IntlShape,
	nodeViewPortalProviderAPI: PortalProviderAPI,
	nodeDecorationRegistry: NodeDecorationFactory[],
	rightSideControlsEnabled = false,
	quickInsertButtonEnabled = true,
): SafePlugin<
	| PluginState
	| {
			activeDropTargetNode: ActiveDropTargetNode | undefined;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			activeNode: any;
			blockMenuOptions:
				| {
						// eslint-disable-next-line @typescript-eslint/no-explicit-any
						canMoveDown: any;
						// eslint-disable-next-line @typescript-eslint/no-explicit-any
						canMoveUp: any;
						// eslint-disable-next-line @typescript-eslint/no-explicit-any
						openedViaKeyboard: any;
				  }
				| undefined;
			decorations: DecorationSet;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			editorHeight: any;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			editorWidthLeft: any;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			editorWidthRight: any;
			isDocSizeLimitEnabled: boolean | null;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			isDragging: any;
			isMenuOpen: boolean | undefined;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			isPMDragging: any;
			isResizerResizing: boolean;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			isSelectedViaDragHandle: any;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			isShiftDown: any;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			lastDragCancelled: any;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			menuTriggerBy: any;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			menuTriggerByNode: any;
			multiSelectDnD: MultiSelectDnD | undefined;
	  }
> => {
	const { formatMessage } = getIntl();
	const isAdvancedLayoutEnabled = editorExperiment('advanced_layouts', true, { exposure: true });
	const toolbarFlagsEnabled = areToolbarFlagsEnabled(Boolean(api?.toolbar));
	const flags: FlagType = {
		toolbarFlagsEnabled,
	};

	let anchorRectCache: AnchorRectCache | undefined;

	if (!isAnchorSupported()) {
		anchorRectCache = new AnchorRectCache();
	}

	let resizeObserverWidth: ResizeObserver;
	let pragmaticCleanup: (() => void) | null = null;

	return new SafePlugin({
		key,
		state: {
			init() {
				return initialState;
			},
			apply: (
				tr: ReadonlyTransaction,
				currentState: PluginState,
				_: EditorState,
				newState: EditorState,
			) =>
				apply(
					api,
					formatMessage,
					tr,
					currentState,
					newState,
					flags,
					nodeViewPortalProviderAPI,
					nodeDecorationRegistry,
					rightSideControlsEnabled,
					quickInsertButtonEnabled,
					anchorRectCache,
					resizeObserverWidth,
					pragmaticCleanup,
				),
		},

		props: {
			decorations: (state: EditorState) => {
				if (api?.limitedMode?.sharedState.currentState()?.enabled) {
					return;
				}

				const isDisabled = api?.editorDisabled?.sharedState.currentState()?.editorDisabled;
				if (isDisabled) {
					const remixRightSideEnabled =
						rightSideControlsEnabled && fg('confluence_remix_button_right_side_block_fg');
					// Hide decorations when disabled, except in view mode when right-side controls are enabled
					if (
						!remixRightSideEnabled ||
						api?.editorViewMode?.sharedState.currentState()?.mode !== 'view'
					) {
						return;
					}
				}
				let decorationSet = key.getState(state)?.decorations;
				// In view mode with right-side controls, remove any lingering drag-handle decorations
				// (created in edit mode) that may not have been cleaned up on mode switch.
				if (
					decorationSet &&
					rightSideControlsEnabled &&
					api?.editorViewMode?.sharedState.currentState()?.mode === 'view'
				) {
					const handleDecs = findHandleDec(decorationSet, 0, state.doc.content.size);
					if (handleDecs.length > 0) {
						decorationSet = decorationSet.remove(handleDecs);
					}
				}
				return decorationSet;
			},
			handleDOMEvents: {
				drop(view: EditorView, event: DragEvent) {
					// Prevent native DnD from triggering if we are in drag
					const { dispatch, dragging, state } = view;
					const tr = state.tr;
					let pluginState = key.getState(state);
					const dndDragCancelled = pluginState?.lastDragCancelled;
					if (pluginState?.isPMDragging || dndDragCancelled) {
						if (fg('platform_editor_ease_of_use_metrics')) {
							api?.metrics?.commands.startActiveSessionTimer()({ tr });
						}

						dispatch(
							tr.setMeta(key, {
								...tr.getMeta(key),
								isPMDragging: false,
								lastDragCancelled: false,
							}),
						);
					}

					pluginState = key.getState(view.state);

					if (!(event.target instanceof HTMLElement) || !pluginState?.activeNode) {
						return false;
					}
					// Currently we can only drag one node at a time
					// so we only need to check first child
					const draggable = dragging?.slice.content.firstChild;
					if (dndDragCancelled || draggable?.type.name === 'layoutColumn') {
						// we prevent native DnD for layoutColumn to prevent single column layout.
						event.preventDefault();
						return false;
					}

					const nodeElement = event.target?.closest?.(`[${getAnchorAttrName()}]`);
					if (!nodeElement) {
						return false;
					}

					// TODO: ED-26959 - Review usage of posAtDOM here
					const domPos = Math.max(view.posAtDOM(nodeElement, 0) - 1, 0);

					const nodeTarget = state.doc.nodeAt(domPos);

					const isSameNode = !!(nodeTarget && draggable?.eq(nodeTarget));

					if (isSameNode) {
						event.preventDefault();
						return true;
					}

					return false;
				},
				dragenter(view: EditorView, event: DragEvent) {
					if (api?.limitedMode?.sharedState.currentState()?.enabled) {
						return;
					}

					// Only process dragenter for block control drags.
					// Other drag types (e.g. table row) should not create
					// drop target decorations or emit active anchors.
					if (!key.getState(view.state)?.isDragging) {
						return;
					}

					if (
						isHTMLElement(event.target) &&
						expValEquals('platform_editor_block_controls_perf_optimization', 'isEnabled', true)
					) {
						const targetElement = event.target.closest('[data-prosemirror-node-name]');
						if (targetElement) {
							const nodeTypeName = targetElement?.getAttribute('data-prosemirror-node-name');

							const pos = view.posAtDOM(targetElement, -1);

							const currentActiveDropTargetNode =
								api?.blockControls.sharedState.currentState()?.activeDropTargetNode;

							if (
								currentActiveDropTargetNode?.pos !== pos ||
								currentActiveDropTargetNode?.nodeTypeName !== nodeTypeName
							) {
								const activeDropTargetNode: ActiveDropTargetNode = {
									pos,
									nodeTypeName,
								};

								view.dispatch(
									view.state.tr.setMeta(key, {
										activeDropTargetNode,
									}),
								);
							}
						}
					} else if (isAdvancedLayoutEnabled) {
						if (isHTMLElement(event.target)) {
							const closestParentElement = event.target.closest(
								'[data-drag-handler-anchor-depth="0"]',
							);

							if (closestParentElement) {
								const currentAnchor = closestParentElement.getAttribute(getAnchorAttrName());

								if (currentAnchor) {
									defaultActiveAnchorTracker.emit(currentAnchor);
								}
							}
						}
					}
				},
				dragstart(view: EditorView) {
					if (api?.limitedMode?.sharedState.currentState()?.enabled) {
						return;
					}

					startMeasure(EDITOR_BLOCKS_DRAG_INIT);

					if (isAdvancedLayoutEnabled) {
						defaultActiveAnchorTracker.reset();
					}

					if (expValEquals('platform_editor_block_controls_perf_optimization', 'isEnabled', true)) {
						anchorRectCache?.clear();
					}

					anchorRectCache?.setEditorView(view);
					view.dispatch(
						view.state.tr.setMeta(key, { ...view.state.tr.getMeta(key), isPMDragging: true }),
					);
				},
				dragend(view: EditorView) {
					if (api?.limitedMode?.sharedState.currentState()?.enabled) {
						return;
					}

					const { state, dispatch } = view;

					if (key.getState(state)?.isPMDragging) {
						const tr = state.tr;
						tr.setMeta(key, { ...state.tr.getMeta(key), isPMDragging: false });
						if (fg('platform_editor_ease_of_use_metrics')) {
							api?.metrics?.commands.startActiveSessionTimer()({ tr });
						}
						dispatch(tr);
					}
				},
				mouseover: (view: EditorView, event: Event) => {
					if (api?.limitedMode?.sharedState.currentState()?.enabled) {
						return;
					}

					// another layer of protection to prevent handling mouseover
					// when native anchor with dnd is enabled in limited mode
					// in case there are descripancies between getNodeIdProvider limited mode state
					if (
						getNodeIdProvider(view)?.isLimitedMode() &&
						expValEquals('platform_editor_native_anchor_with_dnd', 'isEnabled', true)
					) {
						return;
					}

					handleMouseOver(view, event, api);

					return false;
				},
				mousedown: (view: EditorView, event: MouseEvent) => {
					if (api?.limitedMode?.sharedState.currentState()?.enabled) {
						return;
					}

					return editorExperiment('platform_editor_controls', 'variant1')
						? handleMouseDown(api)(view, event)
						: undefined;
				},
				keydown(view: EditorView, event: KeyboardEvent) {
					if (api?.limitedMode?.sharedState.currentState()?.enabled) {
						return;
					}

					if (event.shiftKey && event.ctrlKey) {
						//prevent holding down key combo from firing repeatedly
						if (!event.repeat && boundKeydownHandler(api, formatMessage)(view, event)) {
							event.preventDefault();
							return true;
						}
					}

					if (
						(event.key === 'Enter' || event.key === ' ') &&
						event.target instanceof HTMLElement &&
						editorExperiment('platform_editor_controls', 'variant1')
					) {
						const isDragHandle =
							event.target.closest(
								editorExperiment('platform_editor_block_menu', true)
									? DRAG_HANDLE_SELECTOR
									: '[data-editor-block-ctrl-drag-handle="true"]',
							) !== null;
						api?.core.actions.execute(
							api?.blockControls.commands.setSelectedViaDragHandle(isDragHandle),
						);
					}

					if (
						(event.key === 'ArrowLeft' ||
							event.key === 'ArrowRight' ||
							event.key === 'ArrowDown' ||
							event.key === 'ArrowUp') &&
						editorExperiment('platform_editor_controls', 'variant1')
					) {
						const isBlockMenuOpen =
							api?.blockControls.sharedState.currentState()?.isMenuOpen &&
							editorExperiment('platform_editor_block_menu', true);
						// when block menu is just open, and we press arrow keys, we want to use the arrow keys to navigate the block menu
						// in this scenario, isSelectedViaDragHandle should not be set to false
						if (
							api?.blockControls.sharedState.currentState()?.isSelectedViaDragHandle &&
							!isBlockMenuOpen
						) {
							api?.core.actions.execute(
								api?.blockControls.commands.setSelectedViaDragHandle(false),
							);
						}
					}

					if (
						!event.repeat &&
						event.shiftKey &&
						fg('platform_editor_elements_dnd_shift_click_select')
					) {
						view.dispatch(
							view.state.tr.setMeta(key, { ...view.state.tr.getMeta(key), isShiftDown: true }),
						);
					}

					return false;
				},
				keyup(view: EditorView, event: KeyboardEvent) {
					if (api?.limitedMode?.sharedState.currentState()?.enabled) {
						return;
					}
					if (!event.repeat && event.key === 'Shift') {
						view.dispatch(
							view.state.tr.setMeta(key, { ...view.state.tr.getMeta(key), isShiftDown: false }),
						);
					}
				},
				blur(view: EditorView, event: FocusEvent) {
					if (api?.limitedMode?.sharedState.currentState()?.enabled) {
						return;
					}
					if (editorExperiment('platform_editor_controls', 'variant1')) {
						const isChildOfEditor =
							event.relatedTarget instanceof HTMLElement &&
							event.relatedTarget.closest(`#${EDIT_AREA_ID}`) !== null;

						// don't do anything if the event relatedTarget (the element receiving focus) is a child of the editor
						// or if the editor has focus
						if (isChildOfEditor || view.hasFocus()) {
							return false;
						}

						api?.core?.actions.execute(({ tr }: { tr: Transaction }) => {
							const currMeta = tr.getMeta(key);

							tr.setMeta(key, {
								...currMeta,
								editorBlurred: true,
							});
							return tr;
						});
						return false;
					}
				},
			},
		},
		view: (editorView: EditorView) => {
			const dom = editorView.dom;
			const editorContentArea: HTMLElement | null = editorView.dom.closest(
				'.fabric-editor-popup-scroll-parent',
			);

			// Use ResizeObserver to observe resizer (scroll-parent typically grows when resizing) and editor width changes
			resizeObserverWidth = new ResizeObserver(
				rafSchedule((entries) => {
					const pluginState = key.getState(editorView.state);
					if (!pluginState?.isDragging) {
						const isResizerResizing = !!dom.querySelector('.is-resizing');
						const transaction = editorView.state.tr;

						if (pluginState?.isResizerResizing !== isResizerResizing) {
							transaction.setMeta('is-resizer-resizing', isResizerResizing);
						}

						if (!isResizerResizing) {
							const editorContentArea = entries[0].target;
							// Ignored via go/ees005
							// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
							const editorWidthRight = editorContentArea!.getBoundingClientRect().right;
							// Ignored via go/ees005
							// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
							const editorWidthLeft = editorContentArea!.getBoundingClientRect().left;
							transaction.setMeta(key, {
								...transaction.getMeta(key),
								editorWidthLeft,
								editorWidthRight,
							});
						}
						editorView.dispatch(transaction);
					}
				}),
			);

			const shouldObserve = expValEquals(
				'platform_editor_native_anchor_with_dnd',
				'isEnabled',
				true,
			)
				? !isAnchorSupported()
				: true;

			if (editorContentArea && shouldObserve) {
				resizeObserverWidth.observe(editorContentArea);
			}

			// Start pragmatic monitors
			pragmaticCleanup = destroyFn(api, editorView);

			return {
				destroy() {
					if (editorContentArea) {
						resizeObserverWidth.unobserve(editorContentArea);
					}
					pragmaticCleanup?.();
				},
			};
		},
	});
};

export const getBlockControlsMeta = (tr: Transaction | ReadonlyTransaction) => {
	return tr.getMeta(key) as BlockControlsMeta | undefined;
};
